/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * One read of Firestore config/features per app session, shared by every caller
 * and gated to when a surface needs it. Only a successful read is cached: an
 * unreadable document stays dark (flags null) and the next mount tries again.
 */

import { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { firebaseDB } from "@lemonadetraining/web-shared";
import { CONFIG_DOC, type FeatureFlagsDoc } from "@lemonadetraining/contracts";

interface FeatureFlagsState {
  settled: boolean;
  flags: FeatureFlagsDoc | null;
}

let cachedFlags: FeatureFlagsDoc | null = null;
let pendingRead: Promise<FeatureFlagsDoc> | null = null;

export const resetFeatureFlagsCacheForTests = (): void => {
  cachedFlags = null;
  pendingRead = null;
};

const readFeatureFlags = (): Promise<FeatureFlagsDoc> => {
  if (!pendingRead) {
    pendingRead = getDoc(doc(firebaseDB, "config", CONFIG_DOC.FEATURE_FLAGS)).then(
      (snap) => {
        cachedFlags = snap.exists() ? (snap.data() as FeatureFlagsDoc) : {};
        return cachedFlags;
      },
      (err: unknown) => {
        pendingRead = null;
        throw err;
      }
    );
  }
  return pendingRead;
};

// An unreadable document reads as null, which every flag helper treats as off.
export const readFeatureFlagsOrNull = (): Promise<FeatureFlagsDoc | null> =>
  readFeatureFlags().catch((err: unknown) => {
    console.warn("[FeatureFlags] config/features unavailable", err);
    return null;
  });

export const useFeatureFlags = (
  enabled: boolean
): { flags: FeatureFlagsDoc | null; isLoading: boolean } => {
  const [state, setState] = useState<FeatureFlagsState>(() =>
    cachedFlags ? { settled: true, flags: cachedFlags } : { settled: false, flags: null }
  );
  const shouldRead = enabled && !state.settled;

  useEffect(() => {
    if (!shouldRead) return;
    let isMounted = true;
    readFeatureFlagsOrNull().then((flags) => {
      if (isMounted) setState({ settled: true, flags });
    });
    return () => {
      isMounted = false;
    };
  }, [shouldRead]);

  return { flags: state.flags, isLoading: shouldRead };
};
